<template>
  <div dir="auto" class="relative">
    <div
      class="flex justify-between items-center w-full"
      :class="{ 'mb-2': !field.description }"
    >
      <label class="label text-2xl">
        <MarkdownContent v-if="field.title" :string="field.title" />
        <template v-else>
          {{ showFieldNames && field.name ? field.name : t("signature") }}
        </template>
      </label>
      <div class="space-x-2 flex">
        <span
          v-if="isTextSignature && field.preferences?.format !== 'typed'"
          class="tooltip"
          :data-tip="t('draw_signature')"
        >
          <a
            href="#"
            class="btn btn-outline btn-sm font-medium"
            @click.prevent="toggleTextInput"
          >
            <IconSignature :width="16" />
            <span class="hidden sm:inline">{{ t("draw") }}</span>
          </a>
        </span>
        <span
          v-else-if="
            withTypedSignature &&
            field.preferences?.format !== 'typed' &&
            field.preferences?.format !== 'drawn'
          "
          class="tooltip ml-2"
          :data-tip="t('type_text')"
        >
          <a
            href="#"
            class="btn btn-outline btn-sm font-medium inline-flex flex-nowrap"
            @click.prevent="toggleTextInput"
          >
            <IconTextSize :width="16" />
            <span class="hidden sm:inline">{{ t("type") }}</span>
          </a>
        </span>
        <span
          v-if="
            field.preferences?.format !== 'typed' &&
            field.preferences?.format !== 'drawn'
          "
          class="tooltip"
          :data-tip="t('take_photo')"
        >
          <label
            class="btn btn-outline btn-sm font-medium inline-flex flex-nowrap"
          >
            <IconCamera :width="16" />
            <input
              :key="uploadImageInputKey"
              type="file"
              hidden
              accept="image/*"
              @change="drawImage"
            />
            <span class="hidden sm:inline">{{ t("upload") }}</span>
          </label>
        </span>
        <a
          v-if="modelValue || computedPreviousValue"
          href="#"
          class="btn btn-outline btn-sm font-medium"
          @click.prevent="remove"
        >
          <IconReload :width="16" />
          {{ t("redraw") }}
        </a>
        <span
          v-if="
            withQrButton &&
            !modelValue &&
            !computedPreviousValue &&
            field.preferences?.format !== 'typed'
          "
          class="tooltip"
          :data-tip="t('drawn_signature_on_a_touchscreen_device')"
        >
          <a
            href="#"
            class="btn btn-sm btn-neutral font-medium hidden md:flex"
            :class="{ 'btn-outline': !isShowQr, 'text-white': isShowQr }"
            @click.prevent="
              isShowQr ? hideQr() : [(isTextSignature = false), showQr()]
            "
          >
            <IconQrcode :width="19" :height="19" />
          </a>
        </span>
        <a
          href="#"
          :title="t('minimize')"
          class="py-1.5 inline md:hidden"
          @click.prevent="$emit('minimize')"
        >
          <IconArrowsDiagonalMinimize2 :width="20" :height="20" />
        </a>
      </div>
    </div>
    <div v-if="field.description" dir="auto" class="mb-3 px-1">
      <MarkdownContent :string="field.description" />
    </div>
    <AppearsOn :field="field" />
    <input
      :value="modelValue || computedPreviousValue"
      type="hidden"
      :name="`values[${field.uuid}]`"
    />
    <img
      v-if="modelValue || computedPreviousValue"
      :src="attachmentsIndex[modelValue || computedPreviousValue].url"
      class="mx-auto bg-white border border-base-300 rounded max-h-44"
    />
    <div class="relative">
      <div
        v-if="
          !modelValue &&
          !computedPreviousValue &&
          !isShowQr &&
          !isTextSignature &&
          isSignatureStarted
        "
        class="absolute top-0.5 right-0.5"
      >
        <a
          href="#"
          class="btn btn-ghost font-medium btn-xs md:btn-sm"
          @click.prevent="clear"
        >
          <IconReload :width="16" />
          {{ t("clear") }}
        </a>
      </div>
      <canvas
        v-show="!modelValue && !computedPreviousValue"
        ref="canvas"
        class="bg-white border border-base-300 rounded-2xl w-full"
      />
      <div
        v-show="!modelValue && !computedPreviousValue && !isTextSignature"
        class="mt-2 flex flex-wrap gap-2"
      >
        <span
          class="w-6 h-6 rounded-full cursor-pointer bg-black border border-base-300"
          data-color="#000000"
          @click="drawingColor = '#000000'"
        ></span>
        <span
          class="w-6 h-6 rounded-full cursor-pointer bg-blue-500 border border-base-300"
          data-color="#3b82f6"
          @click="drawingColor = '#3b82f6'"
        ></span>
        <span
          class="w-6 h-6 rounded-full cursor-pointer bg-red-500 border border-base-300"
          data-color="#ef4444"
          @click="drawingColor = '#ef4444'"
        ></span>
        <span
          class="w-6 h-6 rounded-full cursor-pointer bg-white border border-base-300"
          data-color="#ffffff"
          style="box-shadow: 0 0 0 1px #ccc"
          @click="drawingColor = '#ffffff'"
        ></span>
      </div>
      <div
        v-if="isShowQr"
        class="top-0 bottom-0 right-0 left-0 absolute bg-white rounded-2xl m-0.5"
      />
      <div
        v-show="isShowQr"
        class="top-0 bottom-0 right-0 left-0 absolute bg-base-content/10 rounded-2xl"
      >
        <div class="absolute top-1.5 right-1.5 tooltip">
          <a
            href="#"
            class="btn btn-sm btn-circle btn-normal btn-outline"
            @click.prevent="hideQr"
          >
            <IconX />
          </a>
        </div>
        <div class="flex items-center justify-center w-full h-full p-4">
          <div
            v-if="withQrButton"
            ref="qr"
            class="bg-white p-4 rounded-xl h-full"
          >
            <canvas ref="qrCanvas" class="h-full" />
          </div>
        </div>
      </div>
    </div>
    <input
      v-if="isTextSignature"
      id="signature_text_input"
      ref="textInput"
      class="base-input !text-2xl w-full mt-6"
      :required="field.required && !isSignatureStarted && !modelValue && !computedPreviousValue"
      :placeholder="`${t('type_signature_here')}...`"
      type="text"
      @input="updateWrittenSignature"
    />

    <div
      v-if="isTextSignature && $refs.textInput && $refs.textInput.value"
      class="mt-2 flex flex-wrap gap-2 items-center"
    >
      <select
        v-model="selectedFont"
        class="select select-bordered select-sm"
        @change="updateWrittenSignature"
      >
        <option v-for="font in availableFonts" :key="font" :value="font">
          {{ font }}
        </option>
      </select>
      <div class="relative inline-block">
        <input
          v-model.number="fontSize"
          type="number"
          min="1"
          max="100"
          step="1"
          class="input input-bordered input-sm w-20 pr-8"
          @input="updateFontSize"
        />
        <select
          v-model="fontSize"
          class="absolute inset-y-0 right-0 w-8 opacity-0 cursor-pointer"
          @change="updateFontSize"
        >
          <option v-for="size in sortedFontSizes" :key="size" :value="size">
            {{ size }} px
          </option>
        </select>
      </div>
      <button
        class="btn btn-sm"
        :class="{ 'btn-active': isBold }"
        @click.prevent="toggleBold"
      >
        B
      </button>
      <button
        class="btn btn-sm"
        :class="{ 'btn-active': isItalic }"
        @click.prevent="toggleItalic"
      >
        I
      </button>
      <button
        class="btn btn-sm"
        :class="{ 'btn-active': isUnderline }"
        @click.prevent="toggleUnderline"
      >
        U
      </button>
      <div class="flex gap-1">
        <span
          class="w-6 h-6 rounded-full cursor-pointer bg-black border border-base-300"
          data-color="#000000"
          @click="
            textColor = '#000000';
            updateWrittenSignature();
          "
        ></span>
        <span
          class="w-6 h-6 rounded-full cursor-pointer bg-blue-500 border border-base-300"
          data-color="#3b82f6"
          @click="
            textColor = '#3b82f6';
            updateWrittenSignature();
          "
        ></span>
        <span
          class="w-6 h-6 rounded-full cursor-pointer bg-red-500 border border-base-300"
          data-color="#ef4444"
          @click="
            textColor = '#ef4444';
            updateWrittenSignature();
          "
        ></span>
        <span
          class="w-6 h-6 rounded-full cursor-pointer bg-white border border-base-300"
          data-color="#ffffff"
          style="box-shadow: 0 0 0 1px #ccc"
          @click="
            textColor = '#ffffff';
            updateWrittenSignature();
          "
        ></span>
      </div>
    </div>
    <div
      v-if="isShowQr"
      dir="auto"
      class="text-base-content/60 text-xs text-center w-full mt-1"
    >
      {{
        t(
          "scan_the_qr_code_with_the_camera_app_to_open_the_form_on_mobile_and_draw_your_signature"
        )
      }}
    </div>
    <div
      v-else-if="withDisclosure"
      dir="auto"
      class="text-base-content/60 text-xs text-center w-full mt-1"
    >
      {{
        t("by_clicking_you_agree_to_the").replace(
          "{button}",
          buttonText.charAt(0).toUpperCase() + buttonText.slice(1)
        )
      }}
      <a href="https://www.docuseal.co/esign-disclosure" target="_blank">
        <span class="inline md:hidden">{{ t("esignature_disclosure") }}</span>
        <span class="hidden md:inline">{{
          t("electronic_signature_disclosure")
        }}</span>
      </a>
    </div>
    <div v-else class="mt-5 md:mt-7" />
  </div>
</template>

<script>
import {
  IconReload,
  IconCamera,
  IconSignature,
  IconTextSize,
  IconArrowsDiagonalMinimize2,
  IconQrcode,
  IconX,
} from "@tabler/icons-vue";
import { cropCanvasAndExportToPNG } from "./crop_canvas";
import SignaturePad from "signature_pad";
import AppearsOn from "./appears_on";
import MarkdownContent from "./markdown_content";

const scale = 3;

export default {
  name: "SignatureStep",
  components: {
    AppearsOn,
    IconReload,
    IconCamera,
    IconQrcode,
    MarkdownContent,
    IconX,
    IconTextSize,
    IconSignature,
    IconArrowsDiagonalMinimize2,
  },
  inject: ["baseUrl", "t"],
  props: {
    field: { type: Object, required: true },
    submitter: { type: Object, required: true },
    showFieldNames: { type: Boolean, default: true },
    dryRun: { type: Boolean, default: false },
    withDisclosure: { type: Boolean, default: false },
    withQrButton: { type: Boolean, default: false },
    buttonText: { type: String, default: "Submit" },
    withTypedSignature: { type: Boolean, default: true },
    rememberSignature: { type: Boolean, default: false },
    attachmentsIndex: { type: Object, default: () => ({}) },
    previousValue: { type: String, default: "" },
    modelValue: { type: String, default: "" },
    userSignatures: { type: Object, default: () => ({}) }
  },
  emits: ["attached", "update:model-value", "start", "minimize"],
  data() {
    return {
      isSignatureStarted: !!this.previousValue,
      isShowQr: false,
      isUsePreviousValue: true,
      isTextSignature: this.field.preferences?.format === "typed",
      uploadImageInputKey: Math.random().toString(),
      textColor: "#000000",
      drawingColor: "#000000",
      availableFonts: [
        "Arial",
        "Times New Roman",
        "Courier New",
        "Georgia",
        "Lucida Sans",
        "Verdana",
        "Impact",
        "Italianno",
        "Cookie",
        "Parisienne",
      ],
      fontSize:
        parseInt(localStorage.getItem("selectedFontSizeSignature")) || 16,
      selectedFont: localStorage.getItem("selectedFontSignature") || "Arial",
      isBold: false,
      isItalic: false,
      isUnderline: false,
      fontSizes: JSON.parse(localStorage.getItem("fontSizes")) || [
        8, 9, 10, 11, 12, 14, 16, 18, 20, 22, 24, 26, 28, 36, 48, 72,
      ],
      // Store previews for both typed and drawn modes
      typedSignaturePreview: this.userSignatures?.typed || '',
      drawnSignaturePreview: this.userSignatures?.drawn || ''
    };
  },
  computed: {
    submitterSlug() {
      return this.submitter.slug;
    },
    computedPreviousValue() {
      if (!this.isUsePreviousValue) return null;

      // Prefer mode-specific saved signature so drawn mode does not pick typed value.
      const modeKey = this.isTextSignature ? 'typed' : 'drawn';
      const modeSpecificValue = this.userSignatures?.[modeKey];
      if (modeSpecificValue && this.attachmentsIndex[modeSpecificValue]) {
        return modeSpecificValue;
      }

      // If profile signatures are present, do not fallback across modes.
      const hasAnyProfileSignature = !!(this.userSignatures?.typed || this.userSignatures?.drawn);
      if (hasAnyProfileSignature) {
        return null;
      }

      // Fallback to previous submission value when no mode-specific one exists.
      return this.previousValue || null;
    },
    sortedFontSizes() {
      return [...new Set(this.fontSizes)].sort((a, b) => a - b);
    },
  },
  watch: {
    modelValue(newValue) {
      if (newValue) {
        this.isSignatureStarted = true;
      }
    },
    computedPreviousValue(newValue) {
      if (newValue && !this.modelValue) {
        this.isSignatureStarted = true;
      }
    },
    fontSizes: {
      handler(newFontSizes) {
        localStorage.setItem("fontSizes", JSON.stringify(newFontSizes));
      },
      deep: true,
    },
    drawingColor(newColor) {
      if (this.pad) {
        this.pad.penColor = newColor;
      }
    },
  },
  mounted() {
    this.initializeCanvas();
    this.initializeQrCode();
    this.initializeSignaturePad();

    if (this.modelValue || this.computedPreviousValue) {
      this.isSignatureStarted = true;
    }
  },
  beforeUnmount() {
    this.intersectionObserver?.disconnect();
    this.stopCheckSignature();
  },
  methods: {
    initializeCanvas() {
      if (this.$refs.canvas) {
        const canvas = this.$refs.canvas;
        canvas.width = canvas.parentNode.clientWidth * scale;
        canvas.height = (canvas.parentNode.clientWidth * scale) / 3;
        canvas.getContext("2d").scale(scale, scale);

        this.intersectionObserver = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting) {
            this.initializeCanvas();
            this.intersectionObserver?.disconnect();
          }
        });
        this.intersectionObserver.observe(canvas);
      }
    },
    initializeQrCode() {
      if (this.withQrButton) {
        import("qr-creator").then(({ default: Qr }) => {
          Qr.render(
            {
              text: `${document.location.origin}/p/${this.submitterSlug}?f=${
                this.field.uuid.split("-")[0]
              }`,
              radius: 0.0,
              ecLevel: "H",
              background: null,
              size: 132,
            },
            this.$refs.qrCanvas
          );
        });
      }
    },
    initializeSignaturePad() {
      if (this.$refs.canvas) {
        this.pad = new SignaturePad(this.$refs.canvas, {
          penColor: this.drawingColor,
        });
        this.pad.addEventListener("endStroke", () => {
          this.isSignatureStarted = true;
          this.$emit("start");
        });
      }
    },
    remove() {
      this.$emit("update:model-value", "");
      this.isUsePreviousValue = false;
      this.isSignatureStarted = false;
    },
    showQr() {
      this.isShowQr = true;
      this.startCheckSignature();
    },
    hideQr() {
      this.isShowQr = false;
      this.stopCheckSignature();
    },
    startCheckSignature() {
      const after = JSON.stringify(new Date());
      this.checkSignatureInterval = setInterval(
        () => this.checkSignature({ after }),
        2000
      );
    },
    stopCheckSignature() {
      clearInterval(this.checkSignatureInterval);
    },
    async checkSignature(params = {}) {
      const response = await fetch(
        `${this.baseUrl}/s/${this.submitterSlug}/values?field_uuid=${this.field.uuid}&after=${params.after}`,
        { method: "GET" }
      );
      const { attachment } = await response.json();
      if (attachment?.uuid) {
        this.$emit("attached", attachment);
        this.$emit("update:model-value", attachment.uuid);
        this.hideQr();
      }
    },
    clear() {
      this.pad.clear();
      this.isSignatureStarted = false;
      if (this.$refs.textInput) {
        this.$refs.textInput.value = "";
        this.$refs.textInput.focus();
      }
    },
    updateWrittenSignature() {
      if (!this.isTextSignature) return;
      const text = this.$refs.textInput.value;
      this.isSignatureStarted = !!text;
      const canvas = this.$refs.canvas;
      const context = canvas.getContext("2d");
      const fontFamily = this.selectedFont;
      const fontSize = this.fontSize;
      const fontStyle = this.isItalic ? "italic" : "normal";
      const fontWeight = this.isBold ? "bold" : "normal";

      context.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;
      context.textAlign = "center";
      context.textBaseline = "middle";
      context.clearRect(0, 0, canvas.width / scale, canvas.height / scale);
      context.fillStyle = this.textColor;

      const x = canvas.width / (2 * scale);
      const y = canvas.height / (2 * scale);

      context.fillText(text, x, y);

      if (this.isUnderline) {
        const textWidth = context.measureText(text).width;
        const underlineY = y + fontSize / 2;
        context.beginPath();
        context.moveTo(x - textWidth / 2, underlineY);
        context.lineTo(x + textWidth / 2, underlineY);
        context.strokeStyle = this.textColor;
        context.stroke();
      }

      localStorage.setItem("selectedFontSizeSignature", this.fontSize);
      localStorage.setItem("selectedFontSignature", this.selectedFont);
    },
    toggleBold() {
      this.isBold = !this.isBold;
      this.updateWrittenSignature();
    },
    toggleItalic() {
      this.isItalic = !this.isItalic;
      this.updateWrittenSignature();
    },
    toggleUnderline() {
      this.isUnderline = !this.isUnderline;
      this.updateWrittenSignature();
    },
    toggleTextInput() {
      // Toggle the mode WITHOUT removing the current signature
      // Just clear the canvas/input for the new mode
      const previousModelValue = this.modelValue;
      const wasPreviousValueUsed = this.isUsePreviousValue;
      
      this.isTextSignature = !this.isTextSignature;
      this.isSignatureStarted = false;

      // Clear only the input field for the current mode being left
      if (!this.isTextSignature && this.$refs.textInput) {
        this.$refs.textInput.value = '';
      }
      if (this.isTextSignature && this.pad) {
        this.pad.clear();
      }

      // Load user's signature for this format if available
      const modeKey = this.isTextSignature ? 'typed' : 'drawn'
      const userSignatureUuid = this.userSignatures?.[modeKey]
      
      if (userSignatureUuid && this.attachmentsIndex[userSignatureUuid]) {
        // Auto-load user's saved signature for this mode
        this.$emit('update:model-value', userSignatureUuid)
        this.isUsePreviousValue = true
      } else {
        // Restore previous value if available
        this.$emit('update:model-value', previousModelValue)
        this.isUsePreviousValue = wasPreviousValueUsed
      }

      if (this.isTextSignature) {
        this.$nextTick(() => {
          this.$refs.textInput?.focus();
          this.$emit("start");
        });
      }
    },
    drawImage(event) {
      this.remove();
      this.isSignatureStarted = true;
      const file = event.target.files[0];
      if (file && file.type.match("image.*")) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const img = new Image();
          img.src = event.target.result;
          img.onload = () => {
            const canvas = this.$refs.canvas;
            const context = canvas.getContext("2d");
            const aspectRatio = img.width / img.height;
            const canvasWidth = canvas.width / scale;
            const canvasHeight = canvas.height / scale;
            let targetWidth = canvasWidth;
            let targetHeight = canvasHeight;
            if (canvasWidth / canvasHeight > aspectRatio) {
              targetWidth = canvasHeight * aspectRatio;
            } else {
              targetHeight = canvasWidth / aspectRatio;
            }
            if (targetHeight > targetWidth) {
              const scale = targetHeight / targetWidth;
              targetWidth *= scale;
              targetHeight *= scale;
            }
            const x = (canvasWidth - targetWidth) / 2;
            const y = (canvasHeight - targetHeight) / 2;
            setTimeout(() => {
              context.clearRect(0, 0, canvasWidth, canvasHeight);
              context.drawImage(img, x, y, targetWidth, targetHeight);

              // Removed white background
              // Make white pixels transparent
              const imageData = context.getImageData(
                0,
                0,
                canvas.width,
                canvas.height
              );
              const data = imageData.data;
              for (let i = 0; i < data.length; i += 4) {
                if (data[i] > 230 && data[i + 1] > 230 && data[i + 2] > 230) {
                  data[i + 3] = 0;
                }
              }
              context.putImageData(imageData, 0, 0);
              this.$emit("start");
            }, 50);
          };
        };
        reader.readAsDataURL(file);
        this.uploadImageInputKey = Math.random().toString();
      }
    },
    updateFontSize() {
      const newSize = parseInt(this.fontSize);
      if (!isNaN(newSize) && !this.fontSizes.includes(newSize)) {
        this.fontSizes.push(newSize);
        this.fontSizes.sort((a, b) => a - b);
      }
      this.updateWrittenSignature();
    },
    async submit() {
      if (this.modelValue || this.computedPreviousValue) {
        if (this.computedPreviousValue) {
          this.$emit("update:model-value", this.computedPreviousValue);
        }
        return Promise.resolve({});
      }

      return new Promise((resolve, reject) => {
        cropCanvasAndExportToPNG(this.$refs.canvas, { errorOnTooSmall: true })
          .then(async (blob) => {
            const file = new File([blob], "signature.png", {
              type: "image/png",
            });
            if (this.dryRun) {
              const reader = new FileReader();
              reader.readAsDataURL(file);
              reader.onloadend = () => {
                const attachment = {
                  url: reader.result,
                  uuid: Math.random().toString(),
                };
                this.$emit("attached", attachment);
                this.$emit("update:model-value", attachment.uuid);
                resolve(attachment);
              };
            } else {
              const formData = new FormData();
              formData.append("file", file);
              formData.append("submitter_slug", this.submitterSlug);
              formData.append("name", "attachments");
              formData.append("remember_signature", this.rememberSignature);
              
              // Add font metadata if this is a text signature
              if (this.isTextSignature) {
                const fontMetadata = {
                  type: 'text_signature', // Mark as text signature for PDF processing
                  fontFamily: this.selectedFont,
                  fontSize: this.fontSize,
                  fontSizeUnit: 'px', // Indicate that fontSize is in pixels from canvas
                  canvasWidth: this.$refs.canvas ? this.$refs.canvas.width : 400,
                  canvasHeight: this.$refs.canvas ? this.$refs.canvas.height : 200,
                  isBold: this.isBold,
                  isItalic: this.isItalic,
                  isUnderline: this.isUnderline,
                  textColor: this.textColor,
                  text: this.$refs.textInput.value
                };
                formData.append(`font_metadata[${this.field.uuid}]`, JSON.stringify(fontMetadata));
                
                // IMPORTANT: Also update field preferences with font size for consistent display
                if (!this.field.preferences) {
                  this.field.preferences = {}
                }
                this.field.preferences.font_size = this.fontSize
              }
              const response = await fetch(this.baseUrl + "/api/attachments", {
                method: "POST",
                body: formData,
              });

              // Update signature on profile
              await this.updateProfileSignature(formData);

              const attachment = await response.json();
              this.$emit("attached", attachment);
              this.$emit("update:model-value", attachment.uuid);
              this.maybeSetSignedUuid(attachment.signed_uuid);
              resolve(attachment);
            }
          })
          .catch((error) => {
            if (error.message === "Image too small" && !this.field.required) {
              resolve({});
            } else {
              reject(error);
            }
          });
      });
    },
    maybeSetSignedUuid(signedUuid) {
      try {
        if (window.localStorage && signedUuid && this.rememberSignature) {
          const values = window.localStorage.getItem("signed_signature_uuids");
          const data = values ? JSON.parse(values) : {};
          data[this.submitter.email] = signedUuid;
          window.localStorage.setItem(
            "signed_signature_uuids",
            JSON.stringify(data)
          );
        }
      } catch (e) {
        console.error(e);
      }
    },
    async updateProfileSignature(formData) {
      formData.append("signature_format", this.isTextSignature ? "typed" : "drawn");

      await fetch(this.baseUrl + "/user_signature", {
        method: "PUT",
        body: formData,
      });
    },
  },
};
</script>

<style scoped>
@font-face {
  font-family: "Italianno";
  src: url("../fonts/Italianno-Regular.ttf");
  font-weight: normal;
  font-style: normal;
}
@font-face {
  font-family: "Cookie";
  src: url("../fonts/Cookie-Regular.ttf");
  font-weight: normal;
  font-style: normal;
}
@font-face {
  font-family: "Parisienne";
  src: url("../fonts/Parisienne-Regular.ttf");
  font-weight: normal;
  font-style: normal;
}
</style>
